import React from 'react';
import { useTranslation } from 'react-i18next';
import Change from '../../../models/Change';
import type { RoutineAnyOfGroup, RoutineCondition } from '../../../models/Routine';
import { Button } from '../../Button';
import { FieldHelp } from '../../FieldHelp';

const CUSTOM_PATH = '__custom__';

type FieldKind = 'text' | 'status' | 'type';

type ConditionField = {
    path: string;
    kind: FieldKind;
    /** Suggested operators for this field */
    operators?: string[];
    placeholderKey?: string;
};

const CONDITION_FIELDS: ConditionField[] = [
    { path: 'change.type', kind: 'type', operators: ['eq', 'neq', 'in'] },
    { path: 'change.progress.status', kind: 'status', operators: ['eq', 'neq', 'in'] },
    { path: 'change.intake.queueAlias', kind: 'text', operators: ['eq', 'neq', 'contains', 'in'], placeholderKey: 'queue_alias' },
    { path: 'change.intake.sender', kind: 'text', operators: ['eq', 'neq', 'contains', 'regex'], placeholderKey: 'sender' },
    { path: 'change.intake.sender.email', kind: 'text', operators: ['eq', 'neq', 'contains', 'regex'], placeholderKey: 'sender' },
    { path: 'change.slug', kind: 'text', operators: ['eq', 'neq', 'contains', 'regex'], placeholderKey: 'slug' },
    { path: 'change.title.fr', kind: 'text', operators: ['contains', 'eq', 'regex'], placeholderKey: 'title' },
    { path: 'change.title.en', kind: 'text', operators: ['contains', 'eq', 'regex'], placeholderKey: 'title' },
    { path: 'change.assignee._id', kind: 'text', operators: ['eq', 'neq'], placeholderKey: 'assignee_id' },
    { path: 'change.project._id', kind: 'text', operators: ['eq', 'neq'], placeholderKey: 'project_id' },
    { path: 'change.estimate', kind: 'text', operators: ['eq', 'neq'], placeholderKey: 'estimate' },
    { path: 'comment.source', kind: 'text', operators: ['eq', 'neq'], placeholderKey: 'comment_source' },
];

interface ConditionsListEditorProps {
    conditions: RoutineCondition[];
    onChange: (conditions: RoutineCondition[]) => void;
    /** Optional heading override (defaults to AND conditions). */
    title?: string;
    hint?: string;
    emptyHint?: string;
    helpContent?: string;
    /** Compact nesting (no outer blue panel). */
    nested?: boolean;
}

function ConditionsListEditor(props: ConditionsListEditorProps) {
    const {
        conditions,
        onChange,
        title,
        hint,
        emptyHint,
        helpContent,
        nested = false,
    } = props;
    const { t } = useTranslation();

    const knownPaths = CONDITION_FIELDS.map((f) => f.path);

    const fieldMeta = (path: string) =>
        CONDITION_FIELDS.find((f) => f.path === path);

    const updateAt = (index: number, patch: Partial<RoutineCondition>) => {
        const next = [...conditions];
        next[index] = { ...next[index], ...patch };
        onChange(next);
    };

    const removeAt = (index: number) => {
        onChange(conditions.filter((_, j) => j !== index));
    };

    const addCondition = () => {
        onChange([
            ...conditions,
            { path: 'change.type', operator: 'eq', value: '' }
        ]);
    };

    const operatorLabel = (op: string) =>
        t(`projects.routine.condition_operators.${op}`, { defaultValue: op });

    const fieldLabel = (path: string) =>
        t(`projects.routine.condition_fields.${path.replace(/\./g, '_')}`, {
            defaultValue: path
        });

    const renderValueInput = (condition: RoutineCondition, index: number) => {
        const meta = fieldMeta(condition.path);
        const kind = meta?.kind || 'text';
        const rawValue = Array.isArray(condition.value)
            ? condition.value.join(',')
            : (condition.value ?? '');

        if (kind === 'status' && condition.operator !== 'in') {
            return (
                <select
                    className="col-span-5 text-sm border rounded"
                    value={String(condition.value ?? '')}
                    onChange={(e) => updateAt(index, { value: e.target.value })}
                >
                    <option value="">{t('projects.routine.condition_choose_value')}</option>
                    {Change.allStatus().map((status) => (
                        <option key={status} value={status}>
                            {t(`changes.progress_status_type.${status}`) || status}
                        </option>
                    ))}
                </select>
            );
        }

        if (kind === 'type' && condition.operator !== 'in') {
            return (
                <select
                    className="col-span-5 text-sm border rounded"
                    value={String(condition.value ?? '')}
                    onChange={(e) => updateAt(index, { value: e.target.value })}
                >
                    <option value="">{t('projects.routine.condition_choose_value')}</option>
                    {Change.types().map((type) => (
                        <option key={type} value={type}>
                            {t(`projects.${type}`) || type}
                        </option>
                    ))}
                </select>
            );
        }

        const placeholderKey = meta?.placeholderKey
            ? `projects.routine.condition_placeholders.${meta.placeholderKey}`
            : 'projects.routine.condition_placeholders.generic';

        return (
            <input
                className="col-span-5 text-sm border rounded"
                placeholder={t(placeholderKey) || ''}
                value={rawValue}
                onChange={(e) => {
                    const raw = e.target.value;
                    updateAt(index, {
                        value: condition.operator === 'in'
                            ? raw.split(',').map((s) => s.trim()).filter(Boolean)
                            : raw
                    });
                }}
            />
        );
    };

    const operatorsFor = (path: string) => {
        const meta = fieldMeta(path);
        return meta?.operators || ['eq', 'neq', 'contains', 'regex', 'in'];
    };

    const shellClass = nested
        ? 'flex flex-1 flex-col space-y-2'
        : 'flex flex-1 flex-col space-y-2 p-2 pt-0 border rounded bg-blue-50 dark:bg-blue-950/40';

    return (
        <div className={shellClass}>
            {(title || helpContent) && (
                <div className="flex flex-row space-x-1 items-center mt-1 text-gray-900 dark:text-gray-100 font-medium">
                    {title && <h3 className="text-sm">{title}</h3>}
                    {helpContent && <FieldHelp content={helpContent} />}
                </div>
            )}
            {hint && (
                <p className="text-xs text-gray-600 dark:text-gray-300">{hint}</p>
            )}

            {conditions.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    {emptyHint || t('projects.routine.conditions_empty')}
                </p>
            )}

            {conditions.map((condition, i) => {
                const isCustom = !!condition.path && !knownPaths.includes(condition.path);
                const selectedPath = isCustom ? CUSTOM_PATH : (condition.path || 'change.type');
                const ops = operatorsFor(isCustom ? '' : condition.path);

                return (
                    <div key={i} className="flex flex-col gap-1 w-full border border-blue-100 rounded bg-white dark:bg-gray-800 p-2">
                        <div className="grid grid-cols-12 gap-1 w-full items-center">
                            <select
                                className="col-span-5 text-sm border rounded"
                                value={selectedPath}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    if (next === CUSTOM_PATH) {
                                        updateAt(i, { path: '', operator: 'eq', value: '' });
                                        return;
                                    }
                                    const meta = fieldMeta(next);
                                    updateAt(i, {
                                        path: next,
                                        operator: meta?.operators?.[0] || 'eq',
                                        value: ''
                                    });
                                }}
                            >
                                {CONDITION_FIELDS.map((field) => (
                                    <option key={field.path} value={field.path}>
                                        {fieldLabel(field.path)} ({field.path})
                                    </option>
                                ))}
                                <option value={CUSTOM_PATH}>
                                    {t('projects.routine.condition_fields.__custom__')}
                                </option>
                            </select>

                            <select
                                className="col-span-2 text-sm border rounded"
                                value={condition.operator || 'eq'}
                                onChange={(e) => updateAt(i, {
                                    operator: e.target.value,
                                    value: e.target.value === 'in' && !Array.isArray(condition.value)
                                        ? (condition.value ? [String(condition.value)] : [])
                                        : condition.value
                                })}
                            >
                                {ops.map((op) => (
                                    <option key={op} value={op}>{operatorLabel(op)}</option>
                                ))}
                            </select>

                            {renderValueInput(condition, i)}

                            <button
                                type="button"
                                className="col-span-1 text-red-600 text-sm"
                                title={t('common.delete') || ''}
                                onClick={() => removeAt(i)}
                            >
                                ×
                            </button>
                        </div>

                        {isCustom && (
                            <input
                                className="text-sm border rounded w-full"
                                placeholder={t('projects.routine.condition_placeholders.custom_path') || ''}
                                value={condition.path || ''}
                                onChange={(e) => updateAt(i, { path: e.target.value })}
                            />
                        )}
                    </div>
                );
            })}

            <Button
                small
                icon="plus"
                title={t('projects.routine.add_condition')}
                onClick={addCondition}
            />
        </div>
    );
}

interface ConditionsEditorProps {
    conditions: RoutineCondition[];
    onChange: (conditions: RoutineCondition[]) => void;
    anyOf?: RoutineAnyOfGroup[];
    onAnyOfChange?: (anyOf: RoutineAnyOfGroup[]) => void;
}

/**
 * Top-level AND conditions + optional anyOf (OR of AND groups).
 */
function ConditionsEditor(props: ConditionsEditorProps) {
    const { conditions, onChange, anyOf = [], onAnyOfChange } = props;
    const { t } = useTranslation();

    const updateGroup = (index: number, groupConditions: RoutineCondition[]) => {
        if (!onAnyOfChange)
            return;
        const next = [...anyOf];
        next[index] = { conditions: groupConditions };
        onAnyOfChange(next);
    };

    const removeGroup = (index: number) => {
        if (!onAnyOfChange)
            return;
        onAnyOfChange(anyOf.filter((_, j) => j !== index));
    };

    const addGroup = () => {
        if (!onAnyOfChange)
            return;
        onAnyOfChange([
            ...anyOf,
            { conditions: [{ path: 'change.type', operator: 'eq', value: '' }] }
        ]);
    };

    return (
        <div className="flex flex-col space-y-3">
            <ConditionsListEditor
                conditions={conditions}
                onChange={onChange}
                title={t('projects.routine.conditions') || ''}
                hint={t('projects.routine.conditions_hint') || ''}
                emptyHint={t('projects.routine.conditions_empty') || ''}
                helpContent={t('projects.help.routine_conditions') || ''}
            />

            {onAnyOfChange && (
                <div className="flex flex-1 flex-col space-y-2 p-2 pt-0 border rounded bg-amber-50">
                    <div className="flex flex-row space-x-1 items-center mt-1 text-gray-900 dark:text-gray-100 font-medium">
                        <h3 className="text-sm">{t('projects.routine.any_of')}</h3>
                        <FieldHelp content={t('projects.help.routine_any_of') || ''} />
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                        {t('projects.routine.any_of_hint')}
                    </p>

                    {anyOf.length === 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                            {t('projects.routine.any_of_empty')}
                        </p>
                    )}

                    {anyOf.map((group, i) => (
                        <div key={i} className="border border-amber-200 rounded bg-white dark:bg-gray-800 p-2 space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                                    {t('projects.routine.any_of_group', { index: i + 1 })}
                                </span>
                                <button
                                    type="button"
                                    className="text-red-600 text-sm"
                                    title={t('common.delete') || ''}
                                    onClick={() => removeGroup(i)}
                                >
                                    ×
                                </button>
                            </div>
                            <ConditionsListEditor
                                nested
                                conditions={group.conditions || []}
                                onChange={(groupConditions) => updateGroup(i, groupConditions)}
                                emptyHint={t('projects.routine.any_of_group_empty') || ''}
                            />
                        </div>
                    ))}

                    <Button
                        small
                        icon="plus"
                        title={t('projects.routine.add_any_of_group') || ''}
                        onClick={addGroup}
                    />
                </div>
            )}
        </div>
    );
}

export default ConditionsEditor;
